import {useQuery, type QueryClient} from '@tanstack/react-query';
import type {Project} from '@/shared/types/api';
import {projectsApi} from '../services/projects.api';
import {queryKeys} from '@/lib/query-keys';
import {
  clearCached,
  getCached,
  setCached,
  REFERENCE_DATA_CACHE_TTL_MS,
} from '@/shared/lib/localStorageCache';

/** Pass the axios response from `projectsApi.listProjects()` (or equivalent). */
export function normalizeProjectsListResponse(res: unknown): Project[] {
  const payload = (res as {data?: unknown})?.data;
  const projectsData =
    (payload as {data?: unknown})?.data ?? payload ?? [];
  if (!Array.isArray(projectsData)) return [];
  return projectsData.map((p) => ({
    ...p,
    task_count: (p as {task_count?: number}).task_count ?? 0,
  })) as Project[];
}

const PROJECTS_LIST_CACHE_KEY = 'referenceData:projectsList';

// Persisted to localStorage (10-minute TTL) on top of TanStack Query's
// in-memory cache, so the list survives a full page reload/new tab without
// waiting on a network round trip — see localStorageCache.ts.
export async function fetchProjectsList(options?: {refresh?: boolean}): Promise<Project[]> {
  if (!options?.refresh) {
    const cached = getCached<Project[]>(PROJECTS_LIST_CACHE_KEY, REFERENCE_DATA_CACHE_TTL_MS);
    if (cached) return cached;
  }

  const response = await projectsApi.listProjects();
  const list = normalizeProjectsListResponse(response);
  setCached(PROJECTS_LIST_CACHE_KEY, list);
  return list;
}

/**
 * Drops the persisted projects list so the next fetch goes to the network.
 *
 * Invalidating the TanStack Query key alone is NOT enough: the refetch calls
 * `fetchProjectsList()` without `refresh`, which returns the localStorage copy
 * and never reaches the API — the mutation's result stays invisible for up to
 * REFERENCE_DATA_CACHE_TTL_MS. Call this alongside any invalidation of
 * `projectsListQueryKey` after mutating a project.
 */
export function clearProjectsListCache(): void {
  clearCached(PROJECTS_LIST_CACHE_KEY);
}

export const projectsListQueryKey = queryKeys.projects.list();

/**
 * Refresh the projects list after mutating a project — use this instead of
 * invalidating `projectsListQueryKey` on its own.
 *
 * Both layers have to be dropped together. Invalidating only the query key
 * triggers a refetch that calls `fetchProjectsList()` without `refresh`, which
 * returns the persisted copy and never reaches the API, so the mutation stays
 * invisible until the TTL expires — and, because localStorage survives a
 * reload, reloading the page does not help either. Worse, on the paths that
 * also patch the in-memory list optimistically, that refetch overwrites the
 * correct optimistic state with the stale persisted list.
 *
 * Awaiting the returned promise is optional; callers that only need the UI to
 * catch up can fire and forget.
 */
export function invalidateProjectsList(queryClient: QueryClient): Promise<void> {
  clearProjectsListCache();
  return queryClient.invalidateQueries({queryKey: projectsListQueryKey});
}

export function useProjectsListQuery(options?: {enabled?: boolean}) {
  return useQuery({
    queryKey: projectsListQueryKey,
    queryFn: () => fetchProjectsList(),
    enabled: options?.enabled ?? true,
  });
}
